import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";
import { PositionCode, UserRole } from "../../enum/roles";
import { RootState } from "../../store";
import { tokenStorage } from "../../utils/storage";
import Loading from "./Loading";

interface ProtectedRouteProps {
  roles?: string[];
  departments?: string[];
  positions?: string[];
  redirectPath?: string;
  children?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  roles = [],
  departments = [],
  positions = [],
  redirectPath = "/login",
  children,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [isLoading, setIsLoading] = useState(true);
  const hasToken = tokenStorage.get();
  useEffect(() => {
    if (user || !hasToken) {
      setIsLoading(false);
    }
  }, [user, hasToken]);
  const isAuthorized = React.useMemo(() => {
    if (!user?.user) return false;

    if (user.user.role === UserRole.SUPER_ADMIN) return true;
    if (user.user.role === UserRole.ADMIN) {
      if (roles.length > 0) {
        return roles.includes(UserRole.ADMIN);
      }
      return true;
    }
    const hasRequiredRole =
      roles.length === 0 || roles.includes(user.user.role);
    const hasRequiredDepartment =
      departments.length === 0 ||
      departments.includes(user.user.department?.departmentCode);
    const hasRequiredPosition =
      positions.length === 0 ||
      positions.includes(user.user.position?.positionCode) ||
      positions.includes(PositionCode.MANAGER);

    return hasRequiredRole && hasRequiredDepartment && hasRequiredPosition;
  }, [user, roles, departments, positions]);

  if (!hasToken) {
    return <Navigate to={redirectPath} replace />;
  }
  if (isLoading) {
    return <Loading show={true} />;
  }

  if (!isAuthorized) {
    return <Navigate to="/forbidden" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
